// projectlab-web/src/components/ui/Input.tsx
import { forwardRef, InputHTMLAttributes, ReactNode } from 'react'
import { clsx } from 'clsx'

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  hint?: string
  iconLeft?: ReactNode
  iconRight?: ReactNode
  required?: boolean
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, hint, iconLeft, iconRight, required, className, id, ...props }, ref) => {
    const inputId = id ?? label?.toLowerCase().replace(/\s+/g, '-')

    return (
      <div className="flex w-full flex-col gap-2">
        {label ? (
          <label
            htmlFor={inputId}
            className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--color-text-muted)]"
          >
            {label}
            {required ? <span className="ml-0.5 text-[var(--color-error)]">*</span> : null}
          </label>
        ) : null}

        <div className="relative flex items-center">
          {iconLeft ? (
            <span
              className="pointer-events-none absolute left-3 text-[var(--color-text-faint)]"
              aria-hidden
            >
              {iconLeft}
            </span>
          ) : null}

          <input
            ref={ref}
            id={inputId}
            aria-describedby={error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
            aria-invalid={!!error}
            className={clsx(
              'w-full rounded-[1rem] border bg-white text-[var(--color-text)]',
              'min-h-[54px] px-4 text-[15px] leading-6',
              'placeholder:text-[var(--color-text-faint)]',
              'transition-all duration-[180ms] ease-[cubic-bezier(0.16,1,0.3,1)]',
              'focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/16',
              iconLeft ? 'pl-10' : 'pl-4',
              iconRight ? 'pr-10' : 'pr-4',
              error
                ? 'border-[var(--color-error)]/60 bg-white'
                : 'border-[var(--color-border)] hover:border-[var(--color-text-faint)]/60 focus:border-[var(--color-primary)]/28',
              className
            )}
            {...props}
          />

          {iconRight ? (
            <span
              className="pointer-events-none absolute right-3 text-[var(--color-text-faint)]"
              aria-hidden
            >
              {iconRight}
            </span>
          ) : null}
        </div>

        {error ? (
          <p
            id={`${inputId}-error`}
            role="alert"
            className="flex items-center gap-1 text-xs text-[var(--color-error)]"
          >
            <span aria-hidden>✕</span>
            {error}
          </p>
        ) : null}

        {!error && hint ? (
          <p id={`${inputId}-hint`} className="text-xs text-[var(--color-text-muted)]">
            {hint}
          </p>
        ) : null}
      </div>
    )
  }
)

Input.displayName = 'Input'
